import { useTranslation } from 'react-i18next';
import { SectionGridRow } from 'src/common/components/SectionGrid/SectionGridRow';
import { SimData } from 'src/common/components/Sim/constants/sim';
import { RSIM_SIM_LAST_SESSION_SECTION_PROPS } from '../constants/sections';
import { areLabelsExist } from '@/common/utils/areLabelsExist';
import { SectionGridItem } from 'src/common/components/SectionGrid/SectionGridItem';
import { getSessionStatusTranslatedLabel } from 'src/simInventory/utils/sessionStatus';
import Grid from '@mui/material/Grid';
import { NetworkDetails } from 'src/common/components/Sim/Details/NetworkDetails';
import { getNetworkDetailsData } from 'src/common/components/Sim/Details/networkDetails.utils';

type RSimLastSessionDetailsProps = {
  simNameWithLastSession: string | null;
  simWithLastSession: SimData | null;
  primarySimEntry: SimData | null;
  secondarySimEntry: SimData | null;
};

export const RSimLastSessionDetails = ({
  simNameWithLastSession,
  simWithLastSession,
  primarySimEntry,
  secondarySimEntry,
}: RSimLastSessionDetailsProps) => {
  const { t } = useTranslation();

  const simHadSession = !!simNameWithLastSession || !!simWithLastSession;
  const existingSimProfile = primarySimEntry ?? secondarySimEntry;
  const lastSessionSimData = simHadSession ? simWithLastSession : existingSimProfile;
  const lastSessionDetailsVisible =
    lastSessionSimData && areLabelsExist(lastSessionSimData, RSIM_SIM_LAST_SESSION_SECTION_PROPS);

  const simHaveDefinedAnyLastSessionProp =
    areLabelsExist(primarySimEntry, RSIM_SIM_LAST_SESSION_SECTION_PROPS) ||
    areLabelsExist(secondarySimEntry, RSIM_SIM_LAST_SESSION_SECTION_PROPS);

  const forcedLastSessionDetailsVisible = !simHadSession && simHaveDefinedAnyLastSessionProp;
  const { hasNetworkIdentifiers, homeNetwork, lastRoamingNetwork } = simWithLastSession
    ? getNetworkDetailsData(simWithLastSession)
    : {
        hasNetworkIdentifiers: false,
        homeNetwork: undefined,
        lastRoamingNetwork: undefined,
      };

  return lastSessionDetailsVisible ? (
    <>
      <SectionGridRow data-testid="lastSessionDetails" title={t('simInventory.lastSessionDetails')}>
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simNameWithLastSession}
          label={t('common.profile')}
          testId="profile"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={getSessionStatusTranslatedLabel(simWithLastSession?.sessionStatus)}
          label={t('simInventory.sessionStatus')}
          testId="sessionStatus"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.sessionStatusLastUpdated}
          label={t('simInventory.sessionStatusLastUpdated_flat')}
          testId="sessionStatusLastUpdated"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.sessionStartTime}
          label={t('simInventory.sessionStartTime')}
          testId="sessionStart"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.sessionEndTime}
          label={t('simInventory.sessionEndTime')}
          testId="sessionEnd"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.cumulativeTotalUsage}
          label={t('simInventory.cumulativeUsage')}
          testId="cumulativeUsage"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.apn}
          label={t('simInventory.apn')}
          testId="apn"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.localization}
          label={t('simInventory.location')}
          testId="location"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.ratType}
          label={t('simInventory.lastRat')}
          testId="ratType"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.rasClient}
          label={t('simInventory.rasClient')}
          testId="rasClient"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.framedProtocol}
          label={t('simInventory.framedProtocol')}
          testId="framedProtocol"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.nasIpAddress}
          label={t('simInventory.nasIpAddress')}
          testId="nasIpAddress"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.serviceType}
          label={t('simInventory.serviceType')}
          testId="serviceType"
        />
        <SectionGridItem
          forceDisplay={forcedLastSessionDetailsVisible}
          value={simWithLastSession?.userName}
          label={t('simInventory.userName')}
          testId="userName"
        />
      </SectionGridRow>
      {hasNetworkIdentifiers ? (
        <SectionGridRow title={t('simInventory.networkIdentifiers')} data-testid="networkIdentifiers">
          <Grid size={12}>
            <NetworkDetails
              homeNetwork={homeNetwork}
              lastRoamingNetwork={lastRoamingNetwork}
              testIdPrefix="networkIdentifiers"
            />
          </Grid>
        </SectionGridRow>
      ) : null}
    </>
  ) : null;
};
